/**
 * Absolute overlay box in viewport coordinates plus optional vertical push offset.
 */
export type OverlayState = {
    /** Group key currently rendered in overlay; null means overlay is hidden. */
    activeKey: string | null;
    /** Overlay anchor top in viewport coordinates. */
    top: number;
    /** Overlay anchor left in viewport coordinates. */
    left: number;
    /** Overlay width to match list/grid width. */
    width: number;
    /** Negative offset when overlay is pushed by next/last boundaries. */
    offsetY: number;
};

/**
 * Runtime geometry for one group section used to choose sticky candidate.
 */
export type OverlayDescriptor = {
    /** Stable group key. */
    key: string;
    /** Current expanded/collapsed state. */
    isOpen: boolean;
    /** Group header top in viewport coordinates. */
    headerTop: number;
    /** Full group bottom in viewport coordinates (header + children). */
    groupBottom: number;
};

/**
 * Input for sticky group resolution.
 */
type ResolveGroupOverlayParams = {
    /** Full list of known group descriptors in current view. */
    descriptors: OverlayDescriptor[];
    /** Sticky line where overlay should be anchored. */
    stickyTop: number;
    /** Current overlay height, required for push-up calculations. */
    overlayHeight: number;
    /** Optional extra filter (e.g. restrict to top-level mapped groups). */
    isDescriptorEligible?: (descriptor: OverlayDescriptor) => boolean;
};

/**
 * Full resolution output useful for both state updates and debug telemetry.
 */
type ResolveGroupOverlayResult = {
    /** Descriptors that passed eligibility predicate. */
    eligibleDescriptors: OverlayDescriptor[];
    /** Open groups intersecting sticky zone. */
    candidates: OverlayDescriptor[];
    /** Candidate groups whose header already crossed sticky line. */
    anchoredCandidates: OverlayDescriptor[];
    /** Group to render in overlay now. */
    activeCandidate: OverlayDescriptor | null;
    /** Next open group after active one (used for push-up). */
    nextOpenCandidate: OverlayDescriptor | null;
    /** Final translateY offset for the overlay. */
    offsetY: number;
};

/**
 * Tolerance used for geometry comparisons to avoid noisy updates from sub-pixel layout changes.
 */
export const OVERLAY_EPSILON = 0.5;

/**
 * Canonical "hidden" state shared by both desktop and mobile overlays.
 */
export const HIDDEN_OVERLAY_STATE: OverlayState = {
    activeKey: null,
    top: 0,
    left: 0,
    width: 0,
    offsetY: 0,
};

/**
 * Finds the nearest vertical scroll container for the given element.
 * Overlay positioning is always synced to this container.
 */
export const findVerticalScrollParent = (start: HTMLElement | null): HTMLElement | null => {
    if (!start || typeof window === 'undefined') {
        return null;
    }

    let current: HTMLElement | null = start.parentElement;
    while (current) {
        const styles = window.getComputedStyle(current);
        const overflowY = styles.overflowY;
        const canScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';

        if (canScroll) {
            return current;
        }
        current = current.parentElement;
    }

    return null;
};

/**
 * Resolves which group header should be rendered in the sticky overlay.
 *
 * Rules:
 * 1. Consider only eligible descriptors.
 * 2. Group must be open and still intersect the sticky line (`groupBottom > stickyTop`).
 * 3. Header must already cross the sticky line (`headerTop < stickyTop`).
 * 4. Use the last anchored group as active.
 * 5. Compute negative offset so overlay is pushed up by:
 *    - the next open group header, and
 *    - the active group bottom boundary.
 */
export const resolveGroupOverlay = ({
    descriptors,
    stickyTop,
    overlayHeight,
    isDescriptorEligible = () => true,
}: ResolveGroupOverlayParams): ResolveGroupOverlayResult => {
    const sortedDescriptors = [...descriptors].sort((left, right) => left.headerTop - right.headerTop);
    const eligibleDescriptors = sortedDescriptors.filter(isDescriptorEligible);
    const candidates = eligibleDescriptors.filter((descriptor) => (
        descriptor.isOpen && descriptor.groupBottom > stickyTop
    ));
    const anchoredCandidates = candidates.filter((descriptor) => descriptor.headerTop < stickyTop);

    if (anchoredCandidates.length < 1) {
        return {
            eligibleDescriptors,
            candidates,
            anchoredCandidates,
            activeCandidate: null,
            nextOpenCandidate: null,
            offsetY: 0,
        };
    }

    const activeCandidate = anchoredCandidates[anchoredCandidates.length - 1]!;
    const activeIndex = eligibleDescriptors.findIndex((descriptor) => descriptor.key === activeCandidate.key);
    const nextOpenCandidate = activeIndex >= 0
        ? eligibleDescriptors.slice(activeIndex + 1).find((descriptor) => descriptor.isOpen) ?? null
        : null;
    const pushByNextGroup = nextOpenCandidate
        ? nextOpenCandidate.headerTop - stickyTop - overlayHeight
        : 0;
    const pushByActiveGroupBottom = activeCandidate.groupBottom - stickyTop - overlayHeight;
    const offsetY = Math.min(0, pushByNextGroup, pushByActiveGroupBottom);

    return {
        eligibleDescriptors,
        candidates,
        anchoredCandidates,
        activeCandidate,
        nextOpenCandidate,
        offsetY,
    };
};

/**
 * Compares two overlay states with epsilon tolerance to avoid micro-jitter rerenders.
 */
export const isOverlayStateEqual = (
    currentState: OverlayState,
    nextState: OverlayState,
    epsilon = OVERLAY_EPSILON
): boolean => (
    currentState.activeKey === nextState.activeKey
    && Math.abs(currentState.top - nextState.top) < epsilon
    && Math.abs(currentState.left - nextState.left) < epsilon
    && Math.abs(currentState.width - nextState.width) < epsilon
    && Math.abs(currentState.offsetY - nextState.offsetY) < epsilon
);
